<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex justify-between items-center flex-wrap gap-4">
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Shops</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Overview of connected shop integrations</p>
                                </div>
                                <div class="flex items-center gap-2">
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                :disabled="isLoading"
                                                @click="fetchShopIntegrations"
                                        >
                                                <svg class="w-4 h-4" :class="{ 'animate-spin': isLoading }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Refresh
                                        </button>
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                @click="showCreateModal = true"
                                        >
                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                </svg>
                                                Create New
                                        </button>
                                </div>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="p-6">
                        <!-- Search Card -->
                        <div class="bg-white rounded-xl shadow-sm py-4 px-6 mb-4">
                                <div class="flex flex-wrap gap-4 items-center">
                                        <div class="flex-1 min-w-[12rem]">
                                                <div class="relative">
                                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                                v-model="searchQuery"
                                                                type="text"
                                                                placeholder="Search shops..."
                                                                class="w-full py-2 px-3 pl-9 border border-gray-300 rounded-lg text-sm outline-none"
                                                                @input="applyFilters"
                                                        >
                                                </div>
                                        </div>
                                        <div class="flex items-center gap-2">
                                                <span class="text-xs bg-orange-2 text-orange-7 py-1 px-3 rounded-full font-medium">
                                                        {{ filteredShopIntegrations.length }} shops
                                                </span>
                                        </div>
                                </div>
                        </div>

                        <!-- Loading State -->
                        <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span class="ml-3 text-gray-500 text-sm">Loading shops...</span>
                        </div>

                        <!-- Table Card -->
                        <div v-else class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div class="overflow-x-auto">
                                        <table class="w-full border-collapse">
                                                <thead>
                                                        <tr class="bg-gray-50">
                                                                <th
                                                                        v-for="column in columns"
                                                                        :key="column.key"
                                                                        class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 cursor-pointer select-none hover:text-gray-700"
                                                                        :class="column.widthClass"
                                                                        @click="sortBy(column.key)"
                                                                >
                                                                        <span class="inline-flex items-center gap-1">
                                                                                {{ column.label }}
                                                                                <svg v-if="sortKey === column.key" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                        <path v-if="sortDirection === 'asc'" stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
                                                                                        <path v-else stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                                                                                </svg>
                                                                        </span>
                                                                </th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        <tr v-if="paginatedShopIntegrations.length === 0">
                                                                <td colspan="4" class="text-center py-12 px-4">
                                                                        <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .414.336.75.75.75z" />
                                                                        </svg>
                                                                        <p class="text-gray-500 text-sm m-0">No shops found</p>
                                                                </td>
                                                        </tr>
                                                        <tr
                                                                v-for="shopIntegration in paginatedShopIntegrations"
                                                                :key="shopIntegration.id"
                                                                class="border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                                                                @click="editShop(shopIntegration.id)"
                                                        >
                                                                <td class="py-3 px-4 text-sm text-gray-500 font-mono">{{ shopIntegration.id }}</td>
                                                                <td class="py-3 px-4 text-sm text-gray-900 font-medium">{{ shopIntegration.name }}</td>
                                                                <td class="py-3 px-4 text-sm text-gray-700">{{ shopIntegration.integrationType }}</td>
                                                                <td class="py-3 px-4 text-sm text-gray-500">{{ formatDate(shopIntegration.created) }}</td>
                                                        </tr>
                                                </tbody>
                                        </table>
                                </div>

                                <!-- Pagination -->
                                <div v-if="totalPages > 1" class="flex justify-between items-center py-4 px-6 border-t border-gray-200">
                                        <div class="text-sm text-gray-500">
                                                Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, filteredShopIntegrations.length) }} of {{ filteredShopIntegrations.length }} shops
                                        </div>
                                        <div class="flex gap-2">
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === 1"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage--"
                                                >
                                                        Previous
                                                </button>
                                                <button
                                                        v-for="page in visiblePages"
                                                        :key="page"
                                                        type="button"
                                                        class="py-2 px-3 border rounded-md text-sm cursor-pointer min-w-[2.5rem]"
                                                        :class="page === currentPage ? 'bg-orange-7 text-white border-orange-7' : 'bg-white text-gray-700 border-gray-300'"
                                                        @click="currentPage = page"
                                                >
                                                        {{ page }}
                                                </button>
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === totalPages"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage++"
                                                >
                                                        Next
                                                </button>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Create Modal -->
                <div v-if="showCreateModal" class="fixed inset-0 z-50 flex items-center justify-center">
                        <div class="absolute inset-0 bg-black/50" @click="showCreateModal = false"></div>
                        <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-md m-4">
                                <div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
                                        <h3 class="text-lg font-semibold text-gray-900 m-0">Create New Shop Integration</h3>
                                        <button
                                                type="button"
                                                class="text-gray-400 bg-transparent border-none cursor-pointer p-1"
                                                @click="showCreateModal = false"
                                        >
                                                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                        </button>
                                </div>
                                <div class="p-6">
                                        <div class="mb-4">
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
                                                <input
                                                        v-model="newShopName"
                                                        type="text"
                                                        placeholder="Enter shop name"
                                                        class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border"
                                                        @keydown.enter="createShopIntegration"
                                                >
                                        </div>
                                        <div class="mb-4">
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Integration Type</label>
                                                <input
                                                        v-model="newIntegrationType"
                                                        type="text"
                                                        placeholder="e.g. custom, magento, promidata"
                                                        class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border"
                                                        @keydown.enter="createShopIntegration"
                                                >
                                        </div>
                                </div>
                                <div class="flex justify-end gap-3 py-4 px-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                                        <button
                                                type="button"
                                                class="py-2.5 px-4 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm cursor-pointer"
                                                @click="showCreateModal = false"
                                        >
                                                Cancel
                                        </button>
                                        <button
                                                type="button"
                                                :disabled="!newShopName.trim() || !newIntegrationType.trim() || isSaving"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                @click="createShopIntegration"
                                        >
                                                <svg v-if="isSaving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                </svg>
                                                {{ isSaving ? 'Creating...' : 'Create' }}
                                        </button>
                                </div>
                        </div>
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, type Ref, type ComputedRef } from 'vue';
import { useRouter } from 'vue-router';
import Swal from 'sweetalert2';
import ShopIntegrationService, { type ShopIntegrationModel } from 'services/ShopIntegrationService';

export default defineComponent({
        name: 'ShopsPage',
        setup() {
                const router = useRouter();
                const shopIntegrations: Ref<ShopIntegrationModel[]> = ref([]);
                const isLoading: Ref<boolean> = ref(true);
                const isSaving: Ref<boolean> = ref(false);
                const showCreateModal: Ref<boolean> = ref(false);
                const newShopName: Ref<string> = ref('');
                const newIntegrationType: Ref<string> = ref('');

                const searchQuery: Ref<string> = ref('');
                const currentPage: Ref<number> = ref(1);
                const pageSize: number = 20;

                type SortKey = 'id' | 'name' | 'integrationType' | 'created';
                type SortDirection = 'asc' | 'desc';

                const columns: { key: SortKey; label: string; widthClass: string }[] = [
                        { key: 'id', label: 'ID', widthClass: 'w-20' },
                        { key: 'name', label: 'Name', widthClass: '' },
                        { key: 'integrationType', label: 'Integration Type', widthClass: 'w-48' },
                        { key: 'created', label: 'Created', widthClass: 'w-48' },
                ];

                const sortKey: Ref<SortKey> = ref('id');
                const sortDirection: Ref<SortDirection> = ref('asc');

                const sortBy = (key: SortKey): void => {
                        if (sortKey.value === key) {
                                sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
                        } else {
                                sortKey.value = key;
                                sortDirection.value = 'asc';
                        }
                        currentPage.value = 1;
                };

                const filteredShopIntegrations: ComputedRef<ShopIntegrationModel[]> = computed(() => {
                        let result = shopIntegrations.value;
                        if (searchQuery.value.trim()) {
                                const query = searchQuery.value.toLowerCase();
                                result = result.filter((shopIntegration: ShopIntegrationModel) =>
                                        String(shopIntegration.name ?? '').toLowerCase().includes(query) ||
                                        String(shopIntegration.integrationType ?? '').toLowerCase().includes(query) ||
                                        String(shopIntegration.id).includes(query));
                        }
                        const key = sortKey.value;
                        const factor = sortDirection.value === 'asc' ? 1 : -1;
                        return [...result].sort((a: ShopIntegrationModel, b: ShopIntegrationModel) => {
                                if (key === 'id') return (a.id - b.id) * factor;
                                const aValue = String(a[key] ?? '').toLowerCase();
                                const bValue = String(b[key] ?? '').toLowerCase();
                                return aValue.localeCompare(bValue) * factor;
                        });
                });

                const totalPages: ComputedRef<number> = computed(() => Math.ceil(filteredShopIntegrations.value.length / pageSize) || 1);

                const paginatedShopIntegrations: ComputedRef<ShopIntegrationModel[]> = computed(() => {
                        const start = (currentPage.value - 1) * pageSize;
                        return filteredShopIntegrations.value.slice(start, start + pageSize);
                });

                const visiblePages: ComputedRef<number[]> = computed(() => {
                        const pages: number[] = [];
                        const maxVisible = 5;
                        let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
                        const end = Math.min(totalPages.value, start + maxVisible - 1);
                        if (end - start + 1 < maxVisible) {
                                start = Math.max(1, end - maxVisible + 1);
                        }
                        for (let i = start; i <= end; i++) {
                                pages.push(i);
                        }
                        return pages;
                });

                const applyFilters = (): void => {
                        currentPage.value = 1;
                };

                const formatDate = (value: string): string => {
                        if (!value) return '-';
                        const date = new Date(value);
                        if (Number.isNaN(date.getTime())) return value;
                        return date.toLocaleString();
                };

                const fetchShopIntegrations = async (): Promise<void> => {
                        isLoading.value = true;
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                shopIntegrations.value = await ShopIntegrationService.getAll(params);
                        } catch (error: unknown) {
                                const errorMessage = error instanceof Error ? error.message : 'Failed to load shops';
                                Swal.fire({
                                        title: 'Error',
                                        text: errorMessage,
                                        icon: 'error',
                                        confirmButtonColor: '#FF6600',
                                });
                        } finally {
                                isLoading.value = false;
                        }
                };

                const createShopIntegration = async (): Promise<void> => {
                        if (!newShopName.value.trim() || !newIntegrationType.value.trim() || isSaving.value) return;
                        isSaving.value = true;
                        try {
                                await ShopIntegrationService.createIntegration({
                                        name: newShopName.value.trim(),
                                        integrationType: newIntegrationType.value.trim(),
                                });
                                showCreateModal.value = false;
                                newShopName.value = '';
                                newIntegrationType.value = '';
                                await fetchShopIntegrations();
                        } catch (error: unknown) {
                                const errorMessage = error instanceof Error ? error.message : 'Failed to create shop integration';
                                Swal.fire({
                                        title: 'Error',
                                        text: errorMessage,
                                        icon: 'error',
                                        confirmButtonColor: '#FF6600',
                                });
                        } finally {
                                isSaving.value = false;
                        }
                };

                const editShop = (id: number): void => {
                        router.push(`/shop-integration/${id}`);
                };

                onMounted(fetchShopIntegrations);

                return {
                        shopIntegrations,
                        isLoading,
                        searchQuery,
                        currentPage,
                        pageSize,
                        filteredShopIntegrations,
                        totalPages,
                        paginatedShopIntegrations,
                        visiblePages,
                        applyFilters,
                        columns,
                        sortKey,
                        sortDirection,
                        sortBy,
                        formatDate,
                        fetchShopIntegrations,
                        isSaving,
                        showCreateModal,
                        newShopName,
                        newIntegrationType,
                        createShopIntegration,
                        editShop,
                };
        },
});
</script>
